/* eslint-disable  @typescript-eslint/no-explicit-any */
import Vue from "vue";
import Vuex, { StoreOptions, MutationTree, ActionTree, ActionContext } from "vuex";
import { User } from "@/model/User";
import { Utils } from "@/utilities/Utils";
import { ApiClient } from "@/utilities/ApiClient";
import { LookupItem } from "@/model/LookupItem";
import { LookupGroup } from "@/model/Enums";
import { Contractor } from "../model/Contractor";
import createPersistedState from "vuex-persistedstate";


Vue.use(Vuex);

//
// -- This allows us to keep globally available data
//    that we can bind to in components...
//

// Define what we're storing
export interface IStoreState {
    signedInUser: User | null;
    signedInContractor: Contractor | null;
    lookupOneList: Array<LookupItem>;
    lookupTwoList: Array<LookupItem>;
    pendingApiCall: Promise<any> | null;
    bookingID: number | null;
    bookingsSearchText: string | null;
    bookingsStatusID: number | null;
    employeesSearchText: string | null;
    isTestEnviroment: boolean | null;
}

//
// -- build an options object
//

const dataState = createPersistedState({
    // items kept in local storage
    paths: [
        "bookingID",
        "isTestEnviroment"
    ]
});

const options: StoreOptions<IStoreState> = {
    plugins: [dataState],
    state: () => ({
        // shared state
        signedInUser: null as User | null,
        signedInContractor: null as Contractor | null,
        lookupOneList: [] as Array<LookupItem>,
        lookupTwoList: [] as Array<LookupItem>,
        pendingApiCall: null,
        bookingID: null as number | null,
        bookingsSearchText: null,
        bookingsStatusID: null,
        employeesSearchText: null,
        isTestEnviroment: null
    }),
    getters: {
        // computed state
        isSignedIn: (state: IStoreState) => state.signedInUser !== null && !Utils.isEmptyId(state.signedInUser.id),
        signedInUserName: (state: IStoreState) => (state.signedInUser !== null && !Utils.isEmptyId(state.signedInUser.id)) ? state.signedInUser.fullname : "(not signed in)"
    },
    mutations: {},  // added below
    actions: {},    // added below
    modules: {}     // guess we don't need this...
};

// couple of shortcuts (and tell subsequent typescript they're *not* undefined)
const mutations = options.mutations as MutationTree<IStoreState>;
const actions = options.actions as ActionTree<IStoreState, IStoreState>;

//
// -- helper function to streamline the adding of lookups a little bit
//

function addLookup(
    mutations: MutationTree<IStoreState>,
    actions: ActionTree<IStoreState, IStoreState>,
    name: string,
    stateProperty: string,
    url: string) {

    const mutationMethod = "set" + name + "List";
    const actionMethod = "load" + name + "List";
    mutations[mutationMethod] = (state: IStoreState, list: Array<LookupItem>) => {
        (state as any)[stateProperty] = list;
    };
    actions[actionMethod] = async (context: ActionContext<IStoreState, IStoreState>) => {
        const list = await ApiClient.get(url);
        context.commit(mutationMethod, list);
    };
}

// Add all the "basic" lookups
addLookup(mutations, actions, "LookupOne", "lookupOneList", `/api/lookup/search/${LookupGroup.LookupTwo}`);
addLookup(mutations, actions, "LookupTwo", "lookupTwoList", `/api/lookup/search/${LookupGroup.LookupOne}`);

// used during signing back in
mutations.setPendingApiCall = (state: IStoreState, pendingApiCall: Promise<any>) => {
    state.pendingApiCall = pendingApiCall;
};
actions.setPendingApiCall = (context: ActionContext<IStoreState, IStoreState>, pendingApiCall: Promise<any>) => {
    console.log("STORE - action - setPendingApiCall");
    context.commit("setPendingApiCall", pendingApiCall);
};

// the current user - see @/utilities/Authentication module
mutations.setSignedInUser = (state: IStoreState, user: User) => {
    state.signedInUser = user;
};

mutations.setBookingID = (state: IStoreState, bookingID: number) => {
    state.bookingID = bookingID;
};

actions.loadSignedInUser = (context: ActionContext<IStoreState, IStoreState>) => {
    // try and retrieve from session storage
    const userJson = sessionStorage.getItem("signedInUser");
    if (!userJson) return;
    const user = new User(JSON.parse(userJson as string));
    context.commit("setSignedInUser", user);
};
actions.setSignedInUser = (context: ActionContext<IStoreState, IStoreState>, user: User) => {
    sessionStorage.setItem("signedInUser", JSON.stringify(user));
    context.commit("setSignedInUser", user);
};

actions.setBookingID = (context: ActionContext<IStoreState, IStoreState>, bookingID: number) => {
    context.commit("setBookingID", bookingID)
};

mutations.setBookingsSearchText = (state: IStoreState, bookingsSearchText: string) => {
    state.bookingsSearchText = bookingsSearchText;
}
actions.setBookingsSearchText = (context: ActionContext<IStoreState, IStoreState>, bookingsSearchText: string) => {
    context.commit("setBookingsSearchText", bookingsSearchText)
}

mutations.setBookingsStatusID = (state: IStoreState, bookingsStatusID: number) => {
    state.bookingsStatusID = bookingsStatusID;
}
actions.setBookingsStatusID = (context: ActionContext<IStoreState, IStoreState>, bookingsStatusID: number) => {
    context.commit("setBookingsStatusID", bookingsStatusID)
}

mutations.setEmployeesSearchText = (state: IStoreState, employeesSearchText: string) => {
    state.employeesSearchText = employeesSearchText;
}
actions.setEmployeesSearchText = (context: ActionContext<IStoreState, IStoreState>, employeesSearchText: string) => {
    context.commit("setEmployeesSearchText", employeesSearchText)
}
// test eniviroment
mutations.setIsTestEnviroment = (state: IStoreState, isTestEnviroment: boolean) => {
    state.isTestEnviroment = isTestEnviroment;
}
actions.setIsTestEnviroment = (context: ActionContext<IStoreState, IStoreState>, isTestEnviroment: boolean) => {
    context.commit("setIsTestEnviroment", isTestEnviroment);
}
//reset
mutations.reset = (state: IStoreState) => {
    state.bookingsSearchText = null;
    state.bookingsStatusID = null;
    state.employeesSearchText = null;
    state.isTestEnviroment = null;
}

actions.reset = (context: ActionContext<IStoreState, IStoreState>) => {
    context.commit("reset");
}

//
// -- the main deal - export the Store
//

export default new Vuex.Store<IStoreState>(options);
